'use client';

// Shared rich watchlist content for the unified SidePanel (terminal + global
// pages). Source dropdown + gear (sort / logos / flash / futures) + live rows.
// Clicking a row calls onSelectSymbol — the terminal switches the chart; the
// global panel navigates to the stock page. Lifted from the old
// WatchlistSidePanel so the terminal's legacy watchlist is retired.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import useSWR from 'swr';
import { Settings } from 'lucide-react';
import { cn, formatCurrency, formatPct } from '@/lib/utils';
import TickerLogo from '@/components/ticker/TickerLogo';
import TickerLink from '@/components/ticker/TickerLink';
import { useWatchlistPanel, type WatchlistPanelConfig, type FuturesPosition } from '@/context/WatchlistPanelContext';
import { getMarketSession } from '@/lib/marketHours';
import { getWatchlists, getStocksBatch, getCommodityPrices, type CommodityPrice } from '@/lib/api-client';
import {
  resolvePanelTickers, pickDisplayQuote, sortPanelRows, initialFor,
  FUTURES, TRACKED_FUTURES, type WatchlistSource,
} from '@/lib/watchlistPanel';

interface PanelRow {
  ticker: string;
  label: string;
  futureKey?: string;
  price: number | null;
  changePct: number | null;
  extendedHoursPrice: number | null;
  extendedHoursChangePct: number | null;
  isFuture: boolean;
  missing: boolean;
}

interface PanelData {
  lists: { id: string; name: string }[];
  equityRows: PanelRow[];
  futureRows: PanelRow[];
}

async function fetchPanel(source: WatchlistSource, withFutures: boolean): Promise<PanelData> {
  const watchlists = await getWatchlists();
  const lists = watchlists.map((w) => ({ id: w.id, name: w.name }));
  const wlTickers = resolvePanelTickers(watchlists, source);

  const equityTickers = wlTickers.filter((t) => !TRACKED_FUTURES.has(t));
  const needCommodities = withFutures || wlTickers.some((t) => TRACKED_FUTURES.has(t));
  const [batch, commodities] = await Promise.all([
    equityTickers.length ? getStocksBatch(equityTickers) : Promise.resolve([]),
    needCommodities ? getCommodityPrices() : Promise.resolve([] as CommodityPrice[]),
  ]);

  const byTicker = new Map(batch.map((b) => [b.ticker.toUpperCase(), b.data]));
  const byCommodity = new Map(commodities.map((c) => [c.symbol.toUpperCase(), c]));

  const equityRows: PanelRow[] = wlTickers.map((t) => {
    if (TRACKED_FUTURES.has(t)) {
      const meta = FUTURES.find((f) => f.symbol === t);
      const c = byCommodity.get(t);
      return { ticker: t, label: meta?.label ?? t, futureKey: meta?.key, price: c?.price ?? null, changePct: c?.changePct ?? null, extendedHoursPrice: null, extendedHoursChangePct: null, isFuture: true, missing: !c };
    }
    const d = byTicker.get(t);
    return {
      ticker: t, label: t, price: d?.price ?? null, changePct: d?.changePct ?? null,
      extendedHoursPrice: d?.extendedHoursPrice ?? null, extendedHoursChangePct: d?.extendedHoursChangePct ?? null,
      isFuture: false, missing: !d,
    };
  });

  const futureRows: PanelRow[] = withFutures
    ? FUTURES.filter((f) => !wlTickers.includes(f.symbol)).map((f) => {
        const c = byCommodity.get(f.symbol);
        return { ticker: f.symbol, label: f.label, futureKey: f.key, price: c?.price ?? null, changePct: c?.changePct ?? null, extendedHoursPrice: null, extendedHoursChangePct: null, isFuture: true, missing: !c };
      })
    : [];

  return { lists, equityRows, futureRows };
}

export default function PanelWatchlist({ activeSymbol, onSelectSymbol, tickerMode = 'select' }: {
  activeSymbol: string;
  onSelectSymbol: (s: string) => void;
  /** 'select' (terminal) → click loads the symbol; 'popup' (global) → click
   *  opens the app-standard TickerPopup mini-chart, matching stock pages. */
  tickerMode?: 'select' | 'popup';
}) {
  const { source, setSource, config, setConfig } = useWatchlistPanel();
  const [gearOpen, setGearOpen] = useState(false);
  const gearWrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!gearOpen) return;
    const onDown = (e: MouseEvent) => {
      if (gearWrapRef.current && !gearWrapRef.current.contains(e.target as Node)) setGearOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [gearOpen]);

  const withFutures = config.futuresPosition !== 'off';
  const { data, error, isLoading } = useSWR<PanelData>(
    ['side-panel-watchlist', source, withFutures],
    () => fetchPanel(source, withFutures),
    { refreshInterval: () => (getMarketSession() === 'closed' ? 0 : 60_000), revalidateOnFocus: false, dedupingInterval: 30_000 },
  );

  const session = getMarketSession();
  const sessionTag = session === 'pre-market' ? 'PM' : session === 'after-hours' ? 'AH' : null;
  const lists = data?.lists ?? [];

  const rows = useMemo(() => {
    const equities = sortPanelRows(data?.equityRows ?? [], config.sort);
    const selected = new Set(config.futuresSelected);
    const futures = (data?.futureRows ?? []).filter((r) => r.futureKey && selected.has(r.futureKey));
    if (config.futuresPosition === 'top') return [...futures, ...equities];
    if (config.futuresPosition === 'bottom') return [...equities, ...futures];
    return equities;
  }, [data, config.sort, config.futuresPosition, config.futuresSelected]);

  // Flash a row red when its % crosses from up to down between refreshes.
  const prevPct = useRef<Map<string, number>>(new Map());
  const [flashRed, setFlashRed] = useState<ReadonlySet<string>>(new Set());
  useEffect(() => {
    if (!data) return;
    if (!config.flashOnChange) { prevPct.current.clear(); return; }
    const all = [...data.equityRows, ...data.futureRows];
    const turned = new Set<string>();
    for (const r of all) {
      const prev = prevPct.current.get(r.ticker);
      if (prev != null && prev >= 0 && r.changePct != null && r.changePct < 0) turned.add(r.ticker);
      if (r.changePct != null) prevPct.current.set(r.ticker, r.changePct);
    }
    if (turned.size === 0) return;
    setFlashRed(turned);
    const t = setTimeout(() => setFlashRed(new Set()), 1500);
    return () => clearTimeout(t);
  }, [data, config.flashOnChange]);

  return (
    <div className="flex h-full flex-col">
      {/* Source + gear */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-default flex-shrink-0">
        <select
          value={source}
          onChange={(e) => setSource(e.target.value)}
          className="flex-1 min-w-0 bg-surface border border-default rounded-lg px-2 py-1.5 text-xs text-slate-200 focus:outline-none focus:border-sky-500"
          aria-label="Watchlist source"
        >
          <option value="all">All watchlists</option>
          {lists.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
        {sessionTag && (
          <span className="flex-shrink-0 text-[9px] font-medium uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-300" title={session === 'pre-market' ? 'Pre-market prices' : 'After-hours prices'}>
            {sessionTag}
          </span>
        )}
        <div ref={gearWrapRef} className="relative flex-shrink-0">
          <button
            onClick={() => setGearOpen((o) => !o)}
            className={cn('p-1.5 rounded-md transition-colors', gearOpen ? 'text-sky-400 bg-sky-500/10' : 'text-fg-subtle hover:text-white hover:bg-white/5')}
            aria-label="Watchlist settings"
            aria-expanded={gearOpen}
          >
            <Settings size={16} />
          </button>
          {gearOpen && <GearMenu config={config} setConfig={setConfig} />}
        </div>
      </div>

      {/* Rows */}
      <div className="flex-1 overflow-y-auto">
        {isLoading && rows.length === 0 ? (
          <div className="px-3 py-2 space-y-2">
            {Array.from({ length: 10 }).map((_, i) => (
              <div key={i} className="flex items-center gap-2">
                {config.showLogos && <div className="skeleton w-[18px] h-[18px] rounded-full" />}
                <div className="skeleton h-3 w-12 rounded" />
                <div className="skeleton h-3 w-14 rounded ml-auto" />
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="px-4 py-8 text-center text-xs text-fg-subtle">Couldn&apos;t load your watchlist.</div>
        ) : rows.length === 0 ? (
          <div className="px-4 py-8 text-center text-xs text-fg-subtle">
            {lists.length === 0 ? 'No watchlists yet.' : 'This watchlist is empty.'}
          </div>
        ) : (
          rows.map((r) => (
            <WatchRow key={r.ticker} row={r} session={session} showLogo={config.showLogos} flash={flashRed.has(r.ticker)} active={r.ticker === activeSymbol} onSelect={onSelectSymbol} mode={tickerMode} />
          ))
        )}
      </div>
    </div>
  );
}

function Toggle({ checked, onClick }: { checked: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      role="switch"
      aria-checked={checked}
      className={cn('relative inline-flex h-4 w-7 flex-shrink-0 items-center rounded-full transition-colors', checked ? 'bg-sky-500' : 'bg-slate-600')}
    >
      <span className={cn('inline-block h-3 w-3 transform rounded-full bg-white shadow transition-transform', checked ? 'translate-x-3.5' : 'translate-x-0.5')} />
    </button>
  );
}

function GearMenu({ config, setConfig }: { config: WatchlistPanelConfig; setConfig: (patch: Partial<WatchlistPanelConfig>) => void }) {
  const toggleFuture = (key: string) => {
    const set = new Set(config.futuresSelected);
    if (set.has(key)) set.delete(key); else set.add(key);
    setConfig({ futuresSelected: [...set] });
  };
  return (
    <div className="absolute right-0 top-full mt-1 w-56 bg-card-bg border border-default rounded-lg shadow-2xl z-50 p-2.5 space-y-2.5">
      <div>
        <p className="text-[10px] text-fg-subtle uppercase tracking-wide font-medium mb-1">Sort</p>
        <div className="flex gap-1">
          {([['alpha', 'A–Z'], ['change', '% chg']] as const).map(([val, lbl]) => (
            <button key={val} onClick={() => setConfig({ sort: val })}
              className={cn('flex-1 px-2 py-1 text-[11px] rounded-md transition-colors', config.sort === val ? 'bg-sky-500 text-white' : 'bg-surface text-fg-muted hover:text-white')}>
              {lbl}
            </button>
          ))}
        </div>
      </div>
      <div className="border-t border-default/60 pt-2 space-y-1.5">
        <div className="flex items-center justify-between px-1 text-xs text-slate-200">
          <span>Show logos</span>
          <Toggle checked={config.showLogos} onClick={() => setConfig({ showLogos: !config.showLogos })} />
        </div>
        <div className="flex items-center justify-between px-1 text-xs text-slate-200">
          <span>Flash on turn-down</span>
          <Toggle checked={config.flashOnChange} onClick={() => setConfig({ flashOnChange: !config.flashOnChange })} />
        </div>
      </div>
      <div className="border-t border-default/60 pt-2">
        <p className="text-[10px] text-fg-subtle uppercase tracking-wide font-medium mb-1">Futures</p>
        <div className="flex gap-1 mb-2">
          {([['off', 'Off'], ['top', 'Top'], ['bottom', 'Bottom']] as const).map(([val, lbl]) => (
            <button key={val} onClick={() => setConfig({ futuresPosition: val as FuturesPosition })}
              className={cn('flex-1 px-2 py-1 text-[11px] rounded-md transition-colors', config.futuresPosition === val ? 'bg-sky-500 text-white' : 'bg-surface text-fg-muted hover:text-white')}>
              {lbl}
            </button>
          ))}
        </div>
        {config.futuresPosition !== 'off' && (
          <div className="flex flex-wrap gap-1">
            {FUTURES.map((f) => {
              const on = config.futuresSelected.includes(f.key);
              return (
                <button key={f.key} onClick={() => toggleFuture(f.key)}
                  className={cn('px-2 py-1 text-[11px] rounded-md border transition-colors', on ? 'bg-sky-500/15 border-sky-500/40 text-sky-300' : 'bg-surface border-default text-fg-muted hover:text-white')}>
                  {f.label}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function InitialAvatar({ label, size = 18 }: { label: string; size?: number }) {
  return (
    <span className="flex-shrink-0 inline-flex items-center justify-center rounded-full bg-slate-700 text-slate-200 font-semibold"
      style={{ width: size, height: size, fontSize: Math.round(size * 0.5) }} aria-hidden>
      {initialFor(label)}
    </span>
  );
}

function WatchRow({ row, session, showLogo, flash, active, onSelect, mode }: {
  row: PanelRow;
  session: ReturnType<typeof getMarketSession>;
  showLogo: boolean;
  flash: boolean;
  active: boolean;
  onSelect: (s: string) => void;
  mode: 'select' | 'popup';
}) {
  const { price, changePct } = pickDisplayQuote(row, session);
  const up = (changePct ?? 0) >= 0;

  const content = (
    <>
      {showLogo && (
        row.isFuture
          ? <InitialAvatar label={row.label} />
          : <TickerLogo ticker={row.ticker} size={18} className="rounded-full flex-shrink-0 bg-surface" fallback={<InitialAvatar label={row.label} />} />
      )}
      <span className="text-xs font-mono font-semibold text-white truncate">{row.label}</span>
      <div className="ml-auto flex items-center gap-3 flex-shrink-0">
        <span className="text-xs font-mono text-slate-200 tabular-nums">{price != null ? formatCurrency(price) : '—'}</span>
        <span className={cn('text-xs font-mono tabular-nums', changePct == null ? 'text-slate-600' : up ? 'text-[#22c55e]' : 'text-[#ef4444]')}>
          {changePct == null ? '' : formatPct(changePct)}
        </span>
      </div>
    </>
  );

  const rowCls = cn(
    'flex items-center gap-2 pl-3 pr-2 py-1.5 border-b border-default/30 last:border-b-0 transition-colors duration-700',
    flash ? 'bg-[#ef4444]/30' : active ? 'bg-sky-500/10' : 'hover:bg-card-hover-bg',
  );

  // Futures (NQ=F etc.) have no stock page, so they stay non-clickable.
  if (row.isFuture) return <div className={rowCls}>{content}</div>;
  // 'popup' (global): the app-standard TickerLink opens the mini-chart popup on
  // click (no page change), matching the rest of the stock pages.
  if (mode === 'popup') return <TickerLink ticker={row.ticker} className={rowCls}>{content}</TickerLink>;
  // 'select' (terminal): click loads the symbol into the active chart.
  return <button type="button" onClick={() => onSelect(row.ticker)} className={cn(rowCls, 'w-full text-left')}>{content}</button>;
}
